// @ts-nocheck

import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
// eslint-disable-next-line
import videojs from 'video.js';
// eslint-disable-next-line
import videojseme from 'videojs-contrib-eme';
import '!style-loader!css-loader!video.js/dist/video-js.css';

import { isIOSEmbeddedAndStandalone } from '../../../../client';
import { Target } from '../../../../client';
import { StreamModes } from '../../../../constants/picture-in-picture';
import usePictureInPicture from '../../../../hooks/usePictureInPicture';
import { __, getLang } from '../../../../i18n/l10n';
import { NativeBridge, nativeEventTypes } from '../../../../lib/bridge';
import { isIOS } from '../../../../lib/useragent';
import fetch from '../../../../utils/fetch';
import playerErrors from '../../constants/playerErrors';
import ImmersivePlayerError from '../../immersivePlayerError';
import VideoJsError from '../../videoJsError';

import {
  Button,
  Controls,
  ExportStreamIcon,
  LeftContainer,
  Overlay,
  PictureInPictureActivateIcon,
  PictureInPictureCloseIcon,
  PictureInPictureRestoreIcon,
  RightContainer,
  RightContent,
  StyledFullScreen,
  StyledFullScreenExit,
  StyledPlay,
  StyledSpeaker,
  StyledSpeakerMuted,
  TopContainer,
  BottomContainer,
  VideoWrapper,
  PlayWrapper,
  PlayerLoader,
} from './VideoPlayer.style';

const DURATION_TO_HIDE_CONTROLS = 8000; // en ms

// @see https://docs.videojs.com/tutorial-setup.html
// set `inactivityTimeout` to 3000ms to let the user enough time to see the native controls
const VIDEO_PLAYER_DATA_SETUP_CONFIG = `{
  "inactivityTimeout": 0,
  "controlBar": {
    "pictureInPictureToggle": false,
    "fullscreenToggle": false,
    "liveDisplay": false
  }
}`;

const getVideoJsError = (errorCode) => {
  switch (parseInt(errorCode)) {
    case 1:
      return __('You aborted the media playback.');
    case 2:
      return __('A server issue has interrupted media playback.');
    case 3:
      return __('This video could not be loaded as it has unsupported functionalities.');
    case 4:
      return __('The video could not be loaded as it is an unsupported video format or due to a server issue.');
    case 5:
      return __('Acces to media failed.');
    default:
      return __('An error as occurred.');
  }
};

const play = (element) => {
  // On catch silencieusement les erreurs de lecture
  // On ne souhaite pas mettre de await ici car pas besoin d'attendre et de bloquer le reste
  // https://goo.gl/LdLk22
  element.play().catch((e) => {
    console.error('an error as occurred on videoplayer.play()', e?.message);
  });
};

const getDrmKeySystems = (source) => {
  const keySystems = {};
  if (source.drm) {
    if (source.drm.widevine) {
      keySystems['com.widevine.alpha'] = {
        audioRobustness: 'SW_SECURE_CRYPTO',
        videoRobustness: 'SW_SECURE_CRYPTO',
        url: source.drm.widevine,
      };
    }
    if (source.drm.fairplay && source.drm.fairplayCertificate) {
      keySystems['com.apple.fps.1_0'] = {
        certificateUri: source.drm.fairplayCertificate,
        licenseUri: source.drm.fairplay,
      };
    }
    if (source.drm.playready) {
      keySystems['com.microsoft.playready'] = source.drm.playready;
    }

    keySystems['org.w3.clearKey'] = {
      audioContentType: 'audio/webm; codecs="vorbis"',
      videoContentType: 'video/webm; codecs="vp9"',
    };
  }
  return Object.keys(keySystems).length ? keySystems : undefined;
};

const VideoPlayer = ({
  pause = false,
  isFullscreenAvailable = false,
  isFullscreen = false,
  isMultiview = false,
  isPiPFeatureEnabled = false,
  isVolumeAvailable = false,
  canShowControls = false,
  toggleFullscreen,
  enterFullscreen,
  exitFullscreen,
  onClick,
  onMatchStreamExport: onMatchStreamExportFromProps,
  title,
  matchId,
  streamData,
  fetchUrl,
  fetchConfig,
  disableExport = false,
}) => {
  // controls: boolean / show/hide state of controls
  const [showControls, setShowControls] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isMobile, setIsMobile] = useState(Target.__MOBILE__);
  const [response, setResponse] = useState();
  const [url, setUrl] = useState();
  const [ip, setIp] = useState();
  const [error, setError] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [showBigPlayButton, setShowBigPlayButton] = useState(false);

  const videoJsRef = useRef();
  const wrapperDomNodeRef = useRef();
  const pauseFromExitFullscreen = useRef(false);
  const {
    closeStream,
    getStreamWtvOpen,
    isPipCustomActive,
    isPipEnabled,
    isPipNativeActive,
    restoreStream,
    setAskPip,
    setPip,
    type,
    videoDomNodeRef,
    onMatchStreamExport: onMatchStreamExportFromProvider,
  } = usePictureInPicture();

  const onMatchStreamExport = onMatchStreamExportFromProps || onMatchStreamExportFromProvider;
  const isStreamWtvOpen = getStreamWtvOpen?.();
  const showPipControls = isPipCustomActive && !isStreamWtvOpen && !isPipNativeActive;

  const handleRestore = (e) => {
    e.preventDefault();
    e.stopPropagation();
    restoreStream();
  };

  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  // ----- videojs
  useEffect(
    () => {
      // we use `overrideNative`option because HTTP live streaming (HLS) not works as expected for browsers like Safari
      // @see https://github.com/videojs/http-streaming/blob/main/docs/troubleshooting.md#desktop-safariios-safariandroid-chromeedge-exhibit-different-behavior-from-other-browsers
      videoJsRef.current = videojs(videoDomNodeRef.current, {
        fill: true,
        preload: 'auto',
        muted: true,
        language: getLang(),
        html5: {
          vhs: {
            overrideNative: !videojs.browser.IS_ANY_SAFARI,
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
        },
      });

      try {
        // we register the eme plugin by using `registerPlugin` method otherwise videoJsRef.current.eme() returns
        // ... a javascript error (`videoJsRef.current.eme is not a function`)
        if (!videoJsRef.current?.eme) {
          videojs.registerPlugin('eme', videojseme);
        }
        videoJsRef.current.eme();
      } catch (e) {
        console.error(e);
      }

      videoJsRef.current.on('error', () => {
        const error = videoJsRef.current.error();
        const errorStr = getVideoJsError(error.code);
        setError(new VideoJsError(errorStr, { code: error.code, url }));

        // ONLY FOR CUSTOM PiP
        // close the custom PiP when the stream generates an error
        if (isPipCustomActive && !isPipNativeActive && type === StreamModes.MATCH) {
          closeStream();
        }

        videoJsRef.current.error(null);
      });
      videoJsRef.current.on('play', () => {
        setIsPlaying(true);
      });
      videoJsRef.current.on('pause', () => {
        setIsPlaying(false);
        setShowBigPlayButton(true);

        if (pauseFromExitFullscreen.current) {
          play(videoJsRef.current);
          pauseFromExitFullscreen.current = false;
        }
      });
      videoJsRef.current.on('volumechange', () => {
        if (videoJsRef.current.muted()) {
          setIsMuted(true);
        } else {
          setIsMuted(false);
        }
      });
      videoJsRef.current.on('fullscreenchange', () => {
        if (!videoJsRef.current.isFullscreen()) {
          pauseFromExitFullscreen.current = true;
        }
      });
      videoJsRef.current.on('suspend', () => {
        setShowBigPlayButton(true);
      });

      return () => {
        videoJsRef.current.dispose();
      };
    },
    // we don't need `isPipCustomActive`, `isPipNativeActive` and `closeStream` as dependencies
    // ... to avoid the videojs to reinitialize
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videoDomNodeRef],
  );

  // ----- fetching data

  useEffect(() => {
    let didCancel = false;
    const fetchData = async () => {
      try {
        const response = await fetch('https://wapi-ipv6.winamax.fr/core/tools/http-helper/ip');
        const json = await response.json();
        if (!didCancel) {
          setIp(json.data.ip);
        }
      } catch (error) {
        // Log something in sentry here !!
      }
    };

    fetchData();

    return () => {
      didCancel = true;
    };
  }, []);

  useEffect(() => {
    if (ip) {
      videoJsRef.current.pause();
      let didCancel = false;
      const fetchData = async () => {
        try {
          const response = await fetch(fetchUrl, JSON.parse(fetchConfig));
          const json = await response.json();

          if (!didCancel) {
            if ((!json.ECODE || json.ECODE === 0) && !json.error) {
              if ('STREAMS_URL' in json) {
                // Special case for Perform streams #130117
                if (json.PROVIDER === 'PERFORM' && json.PERFORM_STREAM_REGEX && json.STREAMS_URL) {
                  const performResponse = await fetch(json.STREAMS_URL);
                  const performResults = await performResponse.text();
                  const regex = new RegExp(json.PERFORM_STREAM_REGEX, 'gm');
                  const response = [];
                  let matches = null;

                  while ((matches = regex.exec(performResults))) {
                    // On inverse le tableau car on veut les flux HD en premier
                    response.unshift({ url: matches[1], type: 'm3u8' });
                  }

                  if (response.length) {
                    setResponse(response);
                  } else {
                    setError(
                      new ImmersivePlayerError(playerErrors.ERROR_CODE_VIDEO_UNAVAILABLE, {
                        details: 'Response is empty',
                      }),
                    );
                  }
                } else {
                  setError(
                    new ImmersivePlayerError(playerErrors.ERROR_CODE_VIDEO_UNAVAILABLE, {
                      details: 'perform special case fail',
                    }),
                  );
                }
              } else if ('SRC' in json && Array.isArray(json.SRC)) {
                setResponse(json.SRC);
              } else if ('URL' in json) {
                setUrl(json.URL);
              } else if (json.urls) {
                setResponse(json.urls);
              }
              if ('IS_MOBILE' in json) {
                setIsMobile(json.IS_MOBILE);
              }
            } else {
              if (json.ECODE) {
                switch (json.ECODE) {
                  case 1:
                    setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_ACCESS_DENIED));
                    break;
                  case 2:
                    setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_BAD_REQUEST_MISSING_PARAMS));
                    break;
                  case 3:
                    setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_INVALID_REFERENCE));
                    break;
                  case 4:
                    setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_MAINTENANCE));
                    break;
                  case 5:
                    setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_CANNOT_DISPLAY));
                    break;
                  case 6:
                    setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_VIDEO_UNAVAILABLE));
                    break;
                  case 7:
                    setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_BAD_REQUEST_INVALID_PARAMS));
                    break;
                  case 8:
                    setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_STREAM_GEOBLOCKED));
                    break;
                  default:
                    setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_DEFAULT));
                    break;
                }
              }

              switch (json.error?.data?.reason) {
                case playerErrors.ERROR_CANNOT_DISPLAY:
                  setError(new ImmersivePlayerError(playerErrors.ERROR_CANNOT_DISPLAY));
                  break;
                case playerErrors.ERROR_NOT_CONNECTED:
                  setError(new ImmersivePlayerError(playerErrors.ERROR_NOT_CONNECTED));
                  break;
                case playerErrors.ERROR_UNAVAILABLE:
                  setError(new ImmersivePlayerError(playerErrors.ERROR_UNAVAILABLE));
                  break;
                case playerErrors.ERROR_GEO_BLOCKED:
                  setError(new ImmersivePlayerError(playerErrors.ERROR_GEO_BLOCKED));
                  break;
                default:
                  setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_DEFAULT));
                  break;
              }
            }
          }
        } catch (error) {
          if (!didCancel) {
            // FIX ME: Network error ? Generic error ?
            setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_DEFAULT));
          }
        }
      };

      fetchData();

      return () => {
        didCancel = true;
      };
    }
  }, [ip, fetchUrl, fetchConfig]);

  useEffect(() => {
    let didCancel = false;
    const fetchData = async () => {
      if (url) {
        try {
          const response = await fetch(url);
          if (response.status === 200) {
            const json = await response.json();
            if (!didCancel) {
              if ('hlsUrl' in json) {
                setResponse([{ url: json.hlsUrl, type: 'm3u8' }]);
              } else {
                setError(
                  new ImmersivePlayerError(playerErrors.ERROR_CODE_VIDEO_UNAVAILABLE, {
                    url,
                    details: 'no hlsUrl in json',
                    json,
                  }),
                );
              }
            }
          } else if (response.status === 403) {
            if (!didCancel) {
              setError(
                new ImmersivePlayerError(playerErrors.ERROR_CODE_STREAM_GEOBLOCKED, {
                  url,
                  details: 'error 403',
                }),
              );
            }
          } else {
            if (!didCancel) {
              setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_DEFAULT));
            }
          }
        } catch (error) {
          if (!didCancel) {
            setError(new ImmersivePlayerError(playerErrors.ERROR_CODE_DEFAULT));
          }
        }
      }
    };
    fetchData();

    return () => {
      didCancel = true;
    };
  }, [url]);

  useEffect(() => {
    if (response) {
      videoJsRef.current.src(
        response
          .map((src) => {
            const keySystems = getDrmKeySystems(src);
            if (src.type === 'm3u8') {
              return { type: 'application/x-mpegURL', src: src.url, keySystems, withCredentials: !!keySystems };
            } else if (src.type === 'dash') {
              return { type: 'application/dash+xml', src: src.url, keySystems, withCredentials: !!keySystems };
            } else {
              return null;
            }
          })
          .filter((k) => k),
      );
      videoJsRef.current.play();

      // This hack is done because some time the class video-js is not in the dom element, so we must add it manually
      // Without this class, the video does not fill the container cause the fill option is not applied
      const el = videoJsRef.current.el();
      if (!el?.classList.contains('video-js')) {
        el?.classList.add('video-js');
      }
      videoJsRef.current.fill(true);
    }
  }, [response]);

  // only on mobile: add a controls timeout
  useEffect(() => {
    if (isMobile && showControls) {
      const timeout = setTimeout(() => setShowControls(false), DURATION_TO_HIDE_CONTROLS);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isMobile, isMuted, isPlaying, showControls]);

  // Handle first load and pause for multiview mobile
  useEffect(() => {
    if (pause) {
      videoJsRef.current.pause();
    } else {
      play(videoJsRef.current);
    }
  }, [pause]);

  // set the video to ready only when it is fully loaded
  useEffect(() => {
    if (!videoDomNodeRef.current) {
      return;
    }
    videoDomNodeRef.current.onloadedmetadata = () => {
      setIsVideoReady(true);
    };
  }, [videoDomNodeRef]);

  // Button / Video handlers

  /*const handlePlayPauseButtonClick = useCallback(e => {
    e.stopPropagation();

    if (videoJsRef.current.paused()) {
      play(videoJsRef.current);
    } else {
      videoJsRef.current.pause();
    }
  }, []);*/

  const handleMuteVolumeButtonClick = useCallback((e) => {
    e.stopPropagation();

    if (videoJsRef.current.muted()) {
      videoJsRef.current.muted(false);
    } else {
      videoJsRef.current.muted(true);
    }
  }, []);

  const handleFullscreenButtonClick = useCallback(
    (e) => {
      e.stopPropagation();

      if (isIOS) {
        videoJsRef.current.requestFullscreen();
      } else {
        toggleFullscreen();
      }
    },
    [toggleFullscreen],
  );

  const toggleControls = useCallback(() => {
    if (!showControls && canShowControls) {
      setShowControls(true);
    } else {
      setShowControls(false);
    }
  }, [canShowControls, showControls]);

  const handleClickOverlay = useCallback(() => {
    if (isMobile) {
      toggleControls(); // hide and show the controls
    }

    onClick(); // hide and show the immersive player
  }, [isMobile, onClick, toggleControls]);

  const handleMouseOverOverlay = () => {
    if (!isMobile) {
      setShowControls(true);
    }
  };

  const handleMouseOutOverlay = () => {
    if (!isMobile) {
      setShowControls(false);
    }
  };

  const handleClickPip = useCallback(() => {
    // activate the sound when we enter in pip native mode otherwise the video stops when we put the app in background
    if (isPipEnabled && videoJsRef.current.muted()) {
      videoJsRef.current.muted(false);
    }

    setAskPip(true); // this update is necessary for the use of WTV useEffect() in `MatchToolbarController` file
    setPip();
  }, [isPipEnabled, setAskPip, setPip]);

  const handleClickPokerPlayground = useCallback(() => {
    if (typeof onMatchStreamExport === 'function') {
      onMatchStreamExport({ matchId, title, streamData });
    }
  }, [onMatchStreamExport, title, matchId, streamData]);

  const ExternalPictureButton = useMemo(() => {
    if (
      isVideoReady &&
      !isFullscreen &&
      !isMultiview &&
      (onMatchStreamExport || isPiPFeatureEnabled) &&
      !disableExport
    ) {
      const shouldHide = !process.env.REACT_APP_LIB_ENV && isPipCustomActive;
      const onClickHandler = onMatchStreamExport ? handleClickPokerPlayground : handleClickPip;

      return (
        <Button $isPipCustomActive={shouldHide} onClick={onClickHandler}>
          {onMatchStreamExport ? <ExportStreamIcon /> : <PictureInPictureActivateIcon />}
        </Button>
      );
    }
    return null;
  }, [
    isPiPFeatureEnabled,
    isVideoReady,
    isFullscreen,
    isMultiview,
    isPipCustomActive,
    handleClickPip,
    handleClickPokerPlayground,
    onMatchStreamExport,
    disableExport,
  ]);

  const handleOrientationChange = useCallback(
    (data) => {
      if (data?.orientation === 'landscape') {
        if (isIOS) {
          videoJsRef.current.requestFullscreen();
        } else {
          enterFullscreen();
        }
      } else if (data?.orientation === 'portrait') {
        if (isIOS) {
          videoJsRef.current.exitFullscreen();
        } else {
          exitFullscreen();
        }
      }
    },
    [enterFullscreen, exitFullscreen],
  );

  useEffect(() => {
    if (isMobile && Target.__EMBEDDED__) {
      NativeBridge.on(nativeEventTypes.ORIENTATION_CHANGE, handleOrientationChange);
      return () => {
        NativeBridge.off(nativeEventTypes.ORIENTATION_CHANGE, handleOrientationChange);
      };
    }
  }, [handleOrientationChange, isMobile]);

  const handleClickBigPlayButton = useCallback(() => {
    setShowBigPlayButton(false);
    videoJsRef.current.play();
  }, []);

  return (
    <div ref={wrapperDomNodeRef}>
      {!isVideoReady ? <PlayerLoader /> : null}
      <VideoWrapper className="videoWrapper" $isPipNativeActive={isPipNativeActive}>
        <div data-vjs-player>
          <video
            data-setup={VIDEO_PLAYER_DATA_SETUP_CONFIG}
            controls={!isMobile}
            className="video-js"
            ref={videoDomNodeRef}
            playsInline
            poster="noposter"
          />
        </div>
      </VideoWrapper>
      {!isPipNativeActive ? (
        <Overlay onClick={handleClickOverlay} onMouseOver={handleMouseOverOverlay} onMouseOut={handleMouseOutOverlay}>
          <Controls $showPipControls={showPipControls} $isFullscreen={isFullscreen} $isVisible={showControls}>
            <TopContainer $isPipCustomActive={isPipCustomActive}>
              <Button onClick={closeStream}>
                <PictureInPictureCloseIcon />
              </Button>
              {!isFullscreen && showPipControls ? (
                <Button onClick={handleRestore}>
                  <PictureInPictureRestoreIcon />
                </Button>
              ) : null}
            </TopContainer>
            <BottomContainer>
              <LeftContainer $showPipControls={showPipControls}>
                {isVolumeAvailable ? (
                  <Button onClick={handleMuteVolumeButtonClick}>
                    {isMuted ? <StyledSpeakerMuted /> : <StyledSpeaker />}
                  </Button>
                ) : null}
              </LeftContainer>
              <RightContainer $showPipControls={showPipControls} className="videoFullscreenButton">
                <RightContent>
                  {ExternalPictureButton}
                  {(isMobile || isIOSEmbeddedAndStandalone) && isVideoReady && isFullscreenAvailable ? (
                    <Button onClick={handleFullscreenButtonClick}>
                      {isFullscreen ? <StyledFullScreenExit /> : <StyledFullScreen />}
                    </Button>
                  ) : null}
                </RightContent>
              </RightContainer>
            </BottomContainer>
          </Controls>
          {showBigPlayButton && !isPlaying ? (
            <PlayWrapper onClick={handleClickBigPlayButton}>
              <StyledPlay />
            </PlayWrapper>
          ) : null}
        </Overlay>
      ) : null}
    </div>
  );
};

export default VideoPlayer;
